import React, { useState } from 'react';
import { Users, X } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../../utils/helpers';

const PlayerBalancesReport = ({
  playerBalances,
  formatCurrency: formatCurrencyProp,
  clubName,
  expenses = [],
  walletTransactions = [],
  games = [],
  tournaments = [],
  teams = [],
  paymentSubmissions = [],
  players = []
}) => {
  // Use passed formatCurrency or imported one
  const format = formatCurrencyProp || formatCurrency;
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Compute detailed ledger for a player
  const getPlayerLedger = (player) => {
    if (!player) return null;

    const fullPlayer = players.find(p => p.id === player.id) || player;

    const uniquePlayerIds = [
      fullPlayer.id,
      fullPlayer.userId,
      fullPlayer.oderId,
      fullPlayer.oderedPlayerId,
      ...(fullPlayer.matchedPlayerIds || [])
    ].filter(Boolean);

    const playerEmails = [
      fullPlayer.email,
      ...(fullPlayer.matchedPlayerEmails || [])
    ].filter(Boolean).map(e => e.toLowerCase());

    const items = [];
    let totalOwed = 0;

    // 1. Initial donations / Registration fees
    const initialDonationAmounts = expenses.filter(e =>
      (e.isInitialDonation || e.isRegistrationFee) &&
      (e.splitAmong?.some(id => uniquePlayerIds.includes(id)) ||
        (e.playerEmail && playerEmails.includes(e.playerEmail.toLowerCase()))) &&
      (e.approvalStatus === 'approved' || !e.approvalStatus) &&
      !e.isDonation
    );

    initialDonationAmounts.forEach(fee => {
      const amount = fee.amountPerPlayer !== undefined ? fee.amountPerPlayer : fee.amount;
      const label = fee.isInitialDonation ? getRegistrationFeeLabel({ name: clubName }) : (fee.description || 'Registration Fee');
      
      const isDuplicate = items.some(item =>
        item.type === 'registration' &&
        item.description === label &&
        Math.abs(item.amount - amount) < 0.01
      );
      if (isDuplicate) return;

      items.push({
        id: fee.id,
        type: 'registration',
        description: label,
        amount: amount,
        date: fee.createdAt
      });
      totalOwed += amount;
    });

    // 2. Merchandise
    const merchandiseExpenses = expenses.filter(e =>
      e.isMerchandise &&
      uniquePlayerIds.includes(e.userId) &&
      (e.approvalStatus === 'approved' || !e.approvalStatus)
    );

    merchandiseExpenses.forEach(exp => {
      items.push({
        id: exp.id,
        type: 'merchandise',
        description: exp.description || 'Merchandise Purchase',
        amount: exp.amount,
        date: exp.createdAt
      });
      totalOwed += exp.amount;
    });

    // 3. Game Expenses
    const clubGames = games || [];
    clubGames.forEach(game => {
      if (game.playing11?.some(id => uniquePlayerIds.includes(id))) {
        if (game.tournamentId) {
          const tournament = tournaments?.find(t => t.id === game.tournamentId);
          if (tournament && !tournament.isSettled) {
            return; // Skip game expenses until tournament settled
          }
        }

        const gameExpenses = expenses.filter(e =>
          e.gameId === game.id &&
          (e.approvalStatus === 'approved' || !e.approvalStatus) &&
          !e.isInitialDonation &&
          !e.isTournamentFee
        );

        const isSuperSub = game.superSubs?.some(id => uniquePlayerIds.includes(id));

        gameExpenses.forEach(expense => {
          const squadSize = game.playing11?.length || 0;
          const superSubCount = game.superSubs?.length || 0;
          const regularPlayerCount = squadSize - superSubCount;

          let playerShare = 0;
          if (squadSize > 0) {
            const totalShares = regularPlayerCount + (superSubCount * 0.5);
            const perPlayerCost = expense.amount / totalShares;
            playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;
          }

          const roundedShare = Math.round(playerShare * 100) / 100;
          if (roundedShare > 0) {
            items.push({
              id: expense.id,
              type: 'game_expense',
              description: expense.description || 'Game Expense',
              amount: roundedShare,
              date: expense.createdAt,
              gameName: game.name,
              isSuperSub: isSuperSub
            });
            totalOwed += roundedShare;
          }
        });

        // Late fees
        const hasLateArrival = uniquePlayerIds.some(id => game.lateArrivals?.[id]);
        if (game.lateArrivals && hasLateArrival) {
          const lateFee = game.lateFeeAmount ?? 5;
          items.push({
            id: `latefee-${game.id}`,
            type: 'late_fee',
            description: 'Late Fee',
            amount: lateFee,
            date: game.date,
            gameName: game.name
          });
          totalOwed += lateFee;
        }
      }
    });

    // 4. Tournament Fees
    const tournamentWideExpenses = expenses.filter(e =>
      (e.isTournamentFee || !e.gameId || e.level === 'tournament') &&
      e.tournamentId &&
      (e.approvalStatus === 'approved' || !e.approvalStatus)
    );

    const activeTournaments = tournaments || [];
    activeTournaments.forEach(t => {
      const tExpenses = tournamentWideExpenses.filter(e => e.tournamentId === t.id);

      tExpenses.forEach(fee => {
        if (fee.settled && fee.splitAmongDetails?.length > 0) {
          const playerShare = fee.splitAmongDetails.find(p =>
            uniquePlayerIds.includes(p.playerId) ||
            uniquePlayerIds.includes(p.oderId) ||
            (p.email && playerEmails.includes(p.email.toLowerCase())) ||
            (p.name && fullPlayer.name && p.name.toLowerCase().trim() === fullPlayer.name.toLowerCase().trim())
          );

          if (playerShare && playerShare.gamesPlayed > 0) {
            const amount = playerShare.amount || 0;
            const perGameShare = amount / playerShare.gamesPlayed;
            const roundedShare = Math.round(perGameShare * 100) / 100;

            const tournamentGames = clubGames.filter(g => g.tournamentId === t.id);
            const playerGames = tournamentGames.filter(g => g.playing11?.some(id => uniquePlayerIds.includes(id)));

            playerGames.forEach(g => {
              items.push({
                id: `${fee.id}-${g.id}`,
                type: 'tournament_fee',
                description: `Tournament Expenses share`,
                amount: roundedShare,
                date: fee.settledAt || fee.createdAt,
                gameName: g.name || `${g.homeTeam} vs ${g.awayTeam}`,
                tournamentName: t.name,
                gamesPlayed: 1
              });
              totalOwed += roundedShare;
            });
          }
        } else if (t.isSettled) {
          const tournamentGames = clubGames.filter(g => g.tournamentId === t.id);
          const validGames = tournamentGames.filter(g => g.playing11 && g.playing11.length > 0);
          const numGames = validGames.length;

          if (numGames > 0) {
            const expensePerGame = fee.amount / numGames;

            validGames.forEach(g => {
              if (g.playing11?.some(id => uniquePlayerIds.includes(id))) {
                const squadSize = g.playing11.length;
                const superSubCount = g.superSubs?.length || 0;
                const regularCount = squadSize - superSubCount;
                const totalShares = regularCount + (superSubCount * 0.5);

                if (totalShares > 0) {
                  const isSuperSub = g.superSubs?.some(id => uniquePlayerIds.includes(id));
                  const share = isSuperSub ? (expensePerGame / totalShares) * 0.5 : expensePerGame / totalShares;
                  const roundedShare = Math.round(share * 100) / 100;
                  
                  items.push({
                    id: `${fee.id}-${g.id}`,
                    type: 'tournament_fee',
                    description: `Tournament Expenses share`,
                    amount: roundedShare,
                    date: fee.createdAt,
                    gameName: g.name || `${g.homeTeam} vs ${g.awayTeam}`,
                    tournamentName: t.name,
                    gamesPlayed: 1
                  });
                  totalOwed += roundedShare;
                }
              }
            });
          }
        }
      });
    });

    // 5. Payments, waivers, reimbursements (approved)
    const payments = walletTransactions.filter(t =>
      uniquePlayerIds.includes(t.userId) &&
      t.type === 'payment' && t.status === 'approved' && t.amount < 0 && !t.isDonation
    );

    const waivers = walletTransactions.filter(t =>
      uniquePlayerIds.includes(t.userId) &&
      t.type === 'waiver' && t.status === 'approved' && t.amount < 0
    );

    const pendingPayments = (paymentSubmissions || []).filter(p =>
      uniquePlayerIds.includes(p.playerId || p.userId) &&
      p.status === 'pending'
    );

    const walletApprovedExpenses = walletTransactions.filter(t =>
      uniquePlayerIds.includes(t.userId) &&
      t.status === 'approved' &&
      (t.type === 'expense_reimbursement' || (t.type === 'reimbursement' && t.amount > 0))
    ).map(t => {
      const matchingExp = t.expenseId ? expenses.find(e => e.id === t.expenseId) : null;
      return {
        ...t,
        gameId: t.gameId || matchingExp?.gameId,
        gameName: t.gameName || matchingExp?.gameName || matchingExp?.game?.name,
        tournamentName: t.tournamentName || matchingExp?.tournamentName
      };
    });

    // Find any direct expenses that do not have a wallet transaction
    const seenExpenseIds = new Set(walletApprovedExpenses.map(t => t.expenseId).filter(Boolean));
    
    const directExpenses = expenses.filter(e =>
      e.addedByPlayerId && uniquePlayerIds.includes(e.addedByPlayerId) &&
      e.approvalStatus === 'approved' &&
      !e.isRegistrationFee && !e.isTournamentFee && !e.isInitialDonation && !e.isClubCovered && e.paidBy !== 'club' &&
      !seenExpenseIds.has(e.id)
    );

    // Map direct expenses to transaction-like objects
    const syntheticTxns = directExpenses.map(e => ({
      id: `synthetic-${e.id}`,
      description: `Reimbursement: ${e.description || 'Approved Expense'}`,
      amount: e.amount,
      createdAt: e.createdAt,
      expenseId: e.id,
      type: 'expense_reimbursement',
      status: 'approved',
      gameId: e.gameId,
      gameName: e.gameName || e.game?.name,
      tournamentName: e.tournamentName
    }));

    const approvedExpenses = [...walletApprovedExpenses, ...syntheticTxns];

    const reimbursementsReceived = walletTransactions.filter(t =>
      uniquePlayerIds.includes(t.userId) &&
      t.status === 'approved' &&
      (t.type === 'reimbursement_paid' || (t.type === 'reimbursement' && t.amount < 0))
    );

    const totalPaid = [...payments, ...waivers].reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    const totalApprovedExpenses = approvedExpenses.reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalReimbursementsReceived = reimbursementsReceived.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

    const registrationItems = items.filter(i => i.type === 'registration');
    const gameItems = items.filter(i => i.type === 'game_expense' || i.type === 'late_fee');
    const tournamentFeeItems = items.filter(i => i.type === 'tournament_fee');
    const merchandiseItems = items.filter(i => i.type === 'merchandise');

    const displayWaivers = waivers.filter(t =>
      !t.description?.toLowerCase().includes('credit offset') &&
      !t.waiverReason?.toLowerCase().includes('credit offset')
    );

    return {
      registrationItems,
      gameItems,
      tournamentFeeItems,
      merchandiseItems,
      payments,
      waivers: displayWaivers,
      pendingPayments,
      approvedExpenses,
      reimbursementsReceived,
      totalOwed,
      totalPaid,
      totalApprovedExpenses,
      totalReimbursementsReceived,
      netBalance: totalOwed - totalPaid - (totalApprovedExpenses - totalReimbursementsReceived),
      email: fullPlayer.email
    };
  };

  const handleViewLedger = (player) => {
    setSelectedPlayer(player);
  };

  const ledger = selectedPlayer ? getPlayerLedger(selectedPlayer) : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Users size={20} className="text-green-600" />
          Player Balance Report
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Complete breakdown of each player's financial standing. <span className="text-blue-600 font-semibold">Click on a player's name to view their detailed transaction ledger.</span>
        </p>

        {/* Explanation */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">How balances are calculated:</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• <strong>{getRegistrationFeeLabel({ name: clubName })}:</strong> Status (Paid or Due) - tracked separately</li>
            <li>• <strong>Approved Exp.:</strong> Expenses player submitted and paid for the team (club owes them back)</li>
            <li>• <strong>Owes (Games):</strong> Player's share of game expenses:
              <ul className="ml-4 mt-1 space-y-0.5">
                <li>- <strong>T:</strong> Tournament fees (from settled allocations)</li>
                <li>- <strong>G:</strong> Game expenses (snacks, ground fees, etc. split among playing 11/12)</li>
              </ul>
            </li>
            <li>• <strong>Net Balance:</strong> Owes (Games) - Approved Expenses = What they still owe for games</li>
            <li className="pt-1 border-t border-blue-300 mt-2">
              <strong>Positive balance (red):</strong> Player owes the club money
            </li>
            <li>
              <strong>Negative balance (green):</strong> Club owes this player money (credit)
            </li>
            <li className="pt-1 border-t border-blue-300 mt-2">
              <span className="inline-flex items-center gap-1">
                <span className="bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded text-xs font-semibold">External</span>
                External/guest players - no {getRegistrationFeeLabel({ name: clubName }).toLowerCase()}, track game payments manually
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Player
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {getRegistrationFeeLabel({ name: clubName })}
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Approved Exp.
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owes (Games)
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Net Balance
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {playerBalances.map(player => (
              <tr key={player.id} className={`hover:bg-gray-50 ${player.isExternal ? 'bg-amber-50' : ''}`}>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewLedger(player)}
                      className="font-semibold text-blue-600 hover:text-blue-800 text-left hover:underline"
                    >
                      {player.name}
                    </button>
                    {player.isExternal && (
                      <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-semibold">
                        External
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-center">
                  {player.isExternal ? (
                    <span className="text-xs text-gray-400">N/A</span>
                  ) : player.registrationWaived ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
                      🎁 Waived
                    </span>
                  ) : player.registrationFee > 0 ? (
                    player.registrationPaid ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                        ✓ Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-semibold">
                        ${player.registrationFee} Due
                      </span>
                    )
                  ) : (
                    <span className="text-xs text-gray-400">No fee</span>
                  )}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-right">
                  <div className="text-blue-600 font-semibold">
                    {player.paidUpfront > 0 ? format(player.paidUpfront) : '-'}
                  </div>
                  {player.reimbursed > 0 && (
                    <div className="text-xs text-gray-500">
                      (Reimb: {format(player.reimbursed)})
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-right">
                  <div className="text-orange-600 font-semibold">
                    {format(player.owesForExpenses)}
                  </div>
                  {(player.tournamentFees > 0 || player.gameExpenses > 0 || player.merchandiseFees > 0) && (
                    <div className="text-xs text-gray-500">
                      {player.tournamentFees > 0 && `T: ${format(player.tournamentFees)}`}
                      {player.tournamentFees > 0 && (player.gameExpenses > 0 || player.merchandiseFees > 0) && ' + '}
                      {player.gameExpenses > 0 && `G: ${format(player.gameExpenses)}`}
                      {(player.tournamentFees > 0 || player.gameExpenses > 0) && player.merchandiseFees > 0 && ' + '}
                      {player.merchandiseFees > 0 && `M: ${format(player.merchandiseFees)}`}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-right">
                  <div className={`font-bold ${player.balance > 0 ? 'text-red-600' :
                    player.balance < 0 ? 'text-green-600' :
                      'text-gray-600'
                    }`}>
                    {player.balance < 0 && '-'}{format(Math.abs(player.balance))}
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-center">
                  {player.balance > 0 ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Player Owes
                    </span>
                  ) : player.balance < 0 ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Club Owes
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      Settled
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ledger statement modal */}
      {selectedPlayer && ledger && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-bold text-gray-950 text-lg">
                  Statement Ledger: {selectedPlayer.name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Showing detailed transactions for {selectedPlayer.name} ({ledger.email || 'No email'})
                </p>
              </div>
              <button
                onClick={() => setSelectedPlayer(null)}
                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Fees Owed</p>
                  <p className="font-bold text-gray-900 mt-1">{format(ledger.totalOwed)}</p>
                </div>
                <div className="bg-green-50/50 p-3 rounded-xl border border-green-100">
                  <p className="text-[10px] text-green-700 font-semibold uppercase tracking-wider">Payments</p>
                  <p className="font-bold text-green-700 mt-1">-{format(ledger.totalPaid)}</p>
                </div>
                <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                  <p className="text-[10px] text-blue-700 font-semibold uppercase tracking-wider">Approved Exp.</p>
                  <p className="font-bold text-blue-700 mt-1">+{format(ledger.totalApprovedExpenses)}</p>
                </div>
                <div className="bg-amber-50/50 p-3 rounded-xl border border-amber-100">
                  <p className="text-[10px] text-amber-700 font-semibold uppercase tracking-wider">Reimb. Paid</p>
                  <p className="font-bold text-amber-700 mt-1">-{format(ledger.totalReimbursementsReceived)}</p>
                </div>
                <div className={`col-span-2 sm:col-span-1 p-3 rounded-xl border ${
                  ledger.netBalance > 0 
                    ? 'bg-red-50 border-red-200' 
                    : ledger.netBalance < 0 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${
                    ledger.netBalance > 0 ? 'text-red-600' : ledger.netBalance < 0 ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    {ledger.netBalance > 0 ? 'Owed to Club' : ledger.netBalance < 0 ? 'Club Owes' : 'Settled'}
                  </p>
                  <p className={`font-bold mt-1 ${
                    ledger.netBalance > 0 ? 'text-red-700' : ledger.netBalance < 0 ? 'text-green-700' : 'text-gray-700'
                  }`}>
                    {format(Math.abs(ledger.netBalance))}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* 1. Fees Owed Section */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-red-50 border-b border-gray-200 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <h4 className="font-bold text-gray-900 text-sm">Fees Owed</h4>
                    <span className="ml-auto font-bold text-red-600 text-sm">{format(ledger.totalOwed)}</span>
                  </div>

                  {ledger.registrationItems.length === 0 &&
                   ledger.gameItems.length === 0 &&
                   ledger.tournamentFeeItems.length === 0 &&
                   ledger.merchandiseItems.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-xs">No charges/fees recorded</div>
                  ) : (
                    <div className="divide-y divide-gray-100 text-xs">
                      {ledger.registrationItems.map(item => (
                        <div key={item.id} className="flex justify-between items-center px-4 py-2.5 bg-red-50/20">
                          <div>
                            <p className="font-semibold text-gray-800">{item.description}</p>
                            {item.date && <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(item.date)}</p>}
                          </div>
                          <span className="font-bold text-red-600">+{format(item.amount)}</span>
                        </div>
                      ))}

                      {ledger.gameItems.map(item => (
                        <div key={item.id} className="flex justify-between items-center px-4 py-2.5">
                          <div>
                            <p className="font-semibold text-gray-800">{item.description}</p>
                            {item.gameName && <p className="text-[10px] text-gray-500 mt-0.5">{item.gameName}</p>}
                            {item.isSuperSub && (
                              <span className="text-[9px] bg-purple-100 text-purple-700 px-1 py-0.2 rounded font-medium mt-0.5 inline-block">
                                Super Sub (50%)
                              </span>
                            )}
                          </div>
                          <span className="font-bold text-red-600">+{format(item.amount)}</span>
                        </div>
                      ))}

                      {ledger.tournamentFeeItems.map(item => (
                        <div key={item.id} className="flex justify-between items-center px-4 py-2.5 bg-purple-50/20">
                          <div>
                            <p className="font-semibold text-purple-900">🏆 {item.description}</p>
                            {item.gameName ? (
                              <p className="text-[10px] text-gray-500 mt-0.5">{item.gameName}</p>
                            ) : (
                              item.tournamentName && <p className="text-[10px] text-gray-500 mt-0.5">{item.tournamentName}</p>
                            )}
                          </div>
                          <span className="font-bold text-purple-700">+{format(item.amount)}</span>
                        </div>
                      ))}

                      {ledger.merchandiseItems.map(item => (
                        <div key={item.id} className="flex justify-between items-center px-4 py-2.5">
                          <div>
                            <p className="font-semibold text-gray-800">🛍️ {item.description}</p>
                            {item.date && <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(item.date)}</p>}
                          </div>
                          <span className="font-bold text-red-600">+{format(item.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 2. Payments & Waivers Section */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-green-50 border-b border-gray-200 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <h4 className="font-bold text-gray-900 text-sm">Payments & Waivers</h4>
                    <span className="ml-auto font-bold text-green-700 text-sm">-{format(ledger.totalPaid)}</span>
                  </div>

                  {ledger.payments.length === 0 && ledger.waivers.length === 0 && ledger.pendingPayments.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-xs">No payments or waivers recorded</div>
                  ) : (
                    <div className="divide-y divide-gray-100 text-xs">
                      {ledger.payments.map(t => (
                        <div key={t.id} className="flex justify-between items-center px-4 py-2.5">
                          <div>
                            <p className="font-semibold text-gray-800">{t.description || 'Payment'}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {t.paymentMethod && `${t.paymentMethod} · `}{formatDate(t.paymentDate || t.createdAt)}
                            </p>
                          </div>
                          <span className="font-bold text-green-600">-{format(Math.abs(t.amount))}</span>
                        </div>
                      ))}

                      {ledger.waivers.map(t => (
                        <div key={t.id} className="flex justify-between items-center px-4 py-2.5 bg-purple-50/20">
                          <div>
                            <p className="font-semibold text-gray-800">🎁 {t.waiverReason || t.description || 'Fee Waived'}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(t.createdAt)}</p>
                          </div>
                          <span className="font-bold text-purple-600">-{format(Math.abs(t.amount))}</span>
                        </div>
                      ))}

                      {ledger.pendingPayments.map(p => (
                        <div key={p.id} className="flex justify-between items-center px-4 py-2.5 bg-yellow-50/30">
                          <div>
                            <p className="font-semibold text-gray-800">{p.paymentMethod || 'Payment'} · Pending Approval</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(p.paymentDate || p.submittedAt)}</p>
                          </div>
                          <span className="font-bold text-yellow-600">-{format(Math.abs(p.amount))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 3. Approved Expenses Section */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-blue-50 border-b border-gray-200 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    <h4 className="font-bold text-gray-900 text-sm">Approved Expenses (Paid Upfront)</h4>
                    <span className="ml-auto font-bold text-blue-700 text-sm">+{format(ledger.totalApprovedExpenses)}</span>
                  </div>

                  {ledger.approvedExpenses.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-xs">No approved expenses recorded</div>
                  ) : (
                    <div className="divide-y divide-gray-100 text-xs">
                      {ledger.approvedExpenses.map(t => (
                        <div key={t.id} className="flex justify-between items-center px-4 py-2.5 bg-blue-50/10">
                          <div>
                            <p className="font-semibold text-gray-800">{t.description || 'Approved Expense'}</p>
                            {t.gameName && <p className="text-[10px] text-gray-500 mt-0.5">{t.gameName}</p>}
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {formatDate(t.createdAt)}
                            </p>
                          </div>
                          <span className="font-bold text-blue-600">+{format(Math.abs(t.amount))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 4. Reimbursements Received Section */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-amber-50 border-b border-gray-200 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <h4 className="font-bold text-gray-900 text-sm">Reimbursements Received (Payouts)</h4>
                    <span className="ml-auto font-bold text-amber-700 text-sm">-{format(ledger.totalReimbursementsReceived)}</span>
                  </div>

                  {ledger.reimbursementsReceived.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-xs">No reimbursements received</div>
                  ) : (
                    <div className="divide-y divide-gray-100 text-xs">
                      {ledger.reimbursementsReceived.map(t => (
                        <div key={t.id} className="flex justify-between items-center px-4 py-2.5 bg-amber-50/10">
                          <div>
                            <p className="font-semibold text-gray-800">{t.description || 'Reimbursement Payout'}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {formatDate(t.createdAt)}
                            </p>
                          </div>
                          <span className="font-bold text-amber-600">-{format(Math.abs(t.amount))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button
                onClick={() => setSelectedPlayer(null)}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-xs font-semibold shadow transition-colors"
              >
                Close Statement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerBalancesReport;